/**
 * axios setup to use mock service
 */

import axios, { AxiosInstance, AxiosRequestConfig } from "axios";

declare module "axios" {
  export interface AxiosRequestConfig {
    skipServerContext?: boolean;
  }
}

type fetchTypes =
  | "app"
  | "history"
  | "messages"
  | "api"
  | "priority"
  | "newapi"
  | "newapp"
  | "servernode";
export type FetchMethod = "get" | "post" | "put" | "patch" | "delete";

const API_BASE_URLS: { [key in fetchTypes]: string } = {
  app: import.meta.env.VITE_APP_APP_API_URL || "https://app.funner.co.il", //* FOR APP SERVICES
  history:
    import.meta.env.VITE_APP_HISTORY_API_URL || "https://app.funner.co.il", //* FOR HISTORY SERVICES
  messages:
    import.meta.env.VITE_APP_MESSAGES_API_URL || "https://app.funner.co.il", // * FOR MESSAGES SERVICES
  api: import.meta.env.VITE_APP_API_URL || "https://api.funner.co.il", //* FOR API SERVICES
  priority:
    import.meta.env.VITE_APP_PRIORITY_API_URL || "https://api.funner.co.il", //* FOR PRIORITY SERVICES
  newapi:
    import.meta.env.VITE_APP_NEWAPI_API_URL || "https://newapi.funner.co.il", //* FOR NEWAPI SERVICES
  newapp:
    import.meta.env.VITE_APP_NEWAPP_API_URL || "https://newapp.funner.co.il", //* FOR NEWAPP SERVICES
  servernode:
    import.meta.env.VITE_APP_SERVERNODE_API_URL || "http://localhost:3000", //* FOR SERVER NODE SERVICES
};

const RE_SUBDOMAIN = /^(https?:\/\/)([^.]+)(\..+)$/;

/**
 * If localStorage contains a serverContext, it will be added to the baseUrl.
 *   https://app.funner.co.il → https://app2.funner.co.il
 */
export function applyServerContext(baseUrl: string): string {
  // if (!baseUrl) return "";
  
  // Skip server context for IP addresses (like http://109.207.79.184:3000)
  // IP addresses don't match the subdomain pattern, so return as-is
  if (/^https?:\/\/\d+\.\d+\.\d+\.\d+/.test(baseUrl)) {
    return baseUrl;
  }
  
  const raw = localStorage.getItem("serverContext") ?? "0";
  const ctx = Number(raw);
  if (!ctx) return baseUrl;

  const m = baseUrl.match(RE_SUBDOMAIN);
  if (!m) return baseUrl;

  const [, protocol, sub, rest] = m;
  return `${protocol}${sub}${ctx}${rest}`;
}

// ==============================|| FOR APP SERVICES ||============================== //

axios.defaults.headers.common["X-Server-Origin"] =
  import.meta.env.VITE_APP_SERVER_ORIGIN || "server_1";

const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({ baseURL });

  instance.interceptors.request.use(
    async (config) => {
      //* Add server context to baseURL if needed
      if (config.skipServerContext) {
        config.baseURL = baseURL;
      } else {
        config.baseURL = applyServerContext(baseURL);
      }

      //* set token
      const accessToken = localStorage.getItem("whatsapp-token");
      if (accessToken) {
        config.headers["Authorization"] = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        error.response?.status === 401 &&
        !window.location.href.includes("/login")
      ) {
        window.location.pathname = "/login";
      }
      return Promise.reject(error.response?.data || "Request failed");
    }
  );

  return instance;
};

export const axiosAppServices = createAxiosInstance(API_BASE_URLS.app);
export const axiosHistoryServices = createAxiosInstance(API_BASE_URLS.history);
export const axiosMessagesServices = createAxiosInstance(
  API_BASE_URLS.messages
);
export const axiosApiServices = createAxiosInstance(API_BASE_URLS.api);
export const axiosPriorityServices = createAxiosInstance(
  API_BASE_URLS.priority
);
export const axiosNewApiServices = createAxiosInstance(API_BASE_URLS.newapi);
export const axiosNewAppServices = createAxiosInstance(API_BASE_URLS.newapp);
export const axiosServerNodeServices = createAxiosInstance(API_BASE_URLS.servernode);

export async function fetcher(
  method: FetchMethod,
  args: string | [string, AxiosRequestConfig],
  serviceType: fetchTypes,
  unauthorized?: boolean
) {
  try {
    const [url, config = {}] = Array.isArray(args) ? args : [args, {}];

    // Separate `data` and config
    const { data, ...restConfig } = config;

    const requestConfig: AxiosRequestConfig = {
      ...restConfig,
      ...(method === "get" ? { params: data } : {}), // For `GET` providing `params` except of `data`
    };

    const serviceByType: { [key in fetchTypes]: AxiosInstance } = {
      app: axiosAppServices,
      history: axiosHistoryServices,
      messages: axiosMessagesServices,
      api: axiosApiServices,
      priority: axiosPriorityServices,
      newapi: axiosNewApiServices,
      newapp: axiosNewAppServices,
      servernode: axiosServerNodeServices,
    };

    const unathorizedAxios = axios.create({
      baseURL: API_BASE_URLS[serviceType],
    });
    const service = unauthorized
      ? unathorizedAxios
      : serviceByType[serviceType];

    // POST, PUT, PATCH: provide `data` as second arg, and `config` as first one
    if (["post", "put", "patch"].includes(method)) {
      const res = await service[method](url, data, requestConfig);
      return res.data;
    }

    // GET, DELETE: provide `requestConfig` as a second argument
    const res = await service[method](url, requestConfig);
    return res.data;
  } catch (error) {
    const [url] = Array.isArray(args) ? args : [args];
    console.error(url + " fetch error: ", error);
    return null;
  }
}
